/* Theme block enhancement. Content/links remain usable without JavaScript. */
(() => {
  if (customElements.get('gebov-occasion-tabs')) return;
  class GebovOccasionTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      // A deferred asset normally upgrades parsed markup. A microtask also covers editor insertion.
      queueMicrotask(() => { if (this.isConnected && !this.initialized) this.init(); });
    }
    init() {
      this.panels = [...this.querySelectorAll('[data-got-panel]')].filter(p => p.closest('gebov-occasion-tabs') === this);
      this.tabs = this.querySelector('.got__tabs');
      if (!this.tabs || !this.panels.length) return;
      this.initialized = true;
      this.events = new AbortController();
      const listen = (el, event, fn, options = {}) => el.addEventListener(event, fn, {...options, signal: this.events.signal});
      this.tabs.replaceChildren();
      this.buttons = this.panels.map((panel, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'got__tab';
        button.id = `${panel.id}-tab`;
        button.textContent = panel.dataset.gotTitle;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        listen(button, 'click', () => this.select(i, true));
        listen(button, 'keydown', event => {
          const keys = {ArrowRight: (i + 1) % this.panels.length, ArrowLeft: (i - 1 + this.panels.length) % this.panels.length, Home: 0, End: this.panels.length - 1};
          if (keys[event.key] === undefined) return;
          event.preventDefault();
          this.select(keys[event.key], true);
          this.buttons[keys[event.key]].focus();
        });
        this.tabs.append(button);
        const track = panel.querySelector('[data-got-track]');
        if (track) {
          listen(track, 'scroll', () => this.update(panel), {passive: true});
          listen(track, 'keydown', event => {
            if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
            event.preventDefault();
            this.move(panel, (panel.current || 0) + (event.key === 'ArrowRight' ? 1 : -1));
          });
          const prev = panel.querySelector('[data-got-prev]');
          const next = panel.querySelector('[data-got-next]');
          prev.setAttribute('aria-label', this.dataset.labelPrevious);
          next.setAttribute('aria-label', this.dataset.labelNext);
          listen(prev, 'click', () => this.move(panel, panel.current - 1));
          listen(next, 'click', () => this.move(panel, panel.current + 1));
        }
        return button;
      });
      this.classList.add('is-ready');
      this.tabs.hidden = false;
      this.select(Math.min(this.panels.length - 1, Math.max(0, Number(this.dataset.defaultTab || 1) - 1)), false);
      this.resize = new ResizeObserver(() => this.measure(this.panels[this.active]));
      this.resize.observe(this);
      listen(document, 'shopify:block:select', event => {
        const panel = event.target.closest?.('[data-got-panel]') || this.panels.find(p => p.dataset.gotBlockId === event.detail?.blockId);
        const index = this.panels.indexOf(panel);
        if (index >= 0) this.select(index, false);
      });
    }
    select(index, announce) {
      if (!this.panels[index]) return;
      this.active = index;
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
        button.tabIndex = i === index ? 0 : -1;
      });
      const panel = this.panels[index];
      const track = panel.querySelector('[data-got-track]');
      if (track) track.scrollLeft = 0;
      const image = this.querySelector('[data-got-image]');
      const template = panel.querySelector('[data-got-tab-image]') || this.querySelector('[data-got-default-image]');
      image.replaceChildren(template.content.cloneNode(true));
      this.dataset.hasImage = String(Boolean(image.querySelector('img')));
      this.measure(panel);
      if (announce) this.querySelector('[data-got-status]').textContent = panel.dataset.gotTitle;
    }
    measure(panel) {
      const track = panel?.querySelector('[data-got-track]');
      if (!track?.firstElementChild || panel.hidden) return;
      const width = track.clientWidth;
      if (!width) return;
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const stride = first.getBoundingClientRect().width + gap;
      const count = Math.max(1, Math.floor((width + gap + 1) / stride));
      const max = Math.max(0, track.scrollWidth - width);
      const anchors = [];
      for (let i = 0; i < track.children.length; i += count) {
        const x = Math.min(i * stride, max);
        if (!anchors.length || x - anchors.at(-1) > 2) anchors.push(x);
        if (x >= max) break;
      }
      panel.anchors = anchors;
      const markers = panel.querySelector('[data-got-markers]');
      const signature = anchors.map(x => Math.round(x)).join(',');
      if (panel.markerSignature !== signature) {
        panel.markerSignature = signature;
        markers.replaceChildren();
        anchors.forEach((x, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'got__marker';
          button.setAttribute('aria-label', (this.dataset.labelPosition || '[index] / [total]').replace('[index]', i + 1).replace('[total]', anchors.length));
          button.addEventListener('click', () => this.move(panel, i), {signal: this.events.signal});
          markers.append(button);
        });
      }
      panel.querySelector('[data-got-controls]').hidden = anchors.length < 2;
      panel.querySelector('[data-got-hint]').hidden = anchors.length < 2;
      this.update(panel);
    }
    move(panel, index) {
      if (!panel.anchors?.length) return;
      index = Math.min(panel.anchors.length - 1, Math.max(0, index));
      panel.querySelector('[data-got-track]').scrollTo({left: panel.anchors[index], behavior: 'auto'});
      this.update(panel);
    }
    update(panel) {
      if (!panel.anchors?.length || panel.hidden) return;
      const track = panel.querySelector('[data-got-track]');
      panel.current = panel.anchors.reduce((best, x, i) => Math.abs(x - track.scrollLeft) < Math.abs(panel.anchors[best] - track.scrollLeft) ? i : best, 0);
      panel.querySelectorAll('.got__marker').forEach((button, i) => button.setAttribute('aria-current', String(i === panel.current)));
      panel.querySelector('[data-got-prev]').disabled = panel.current === 0;
      panel.querySelector('[data-got-next]').disabled = panel.current === panel.anchors.length - 1;
      panel.querySelector('[data-got-count]').textContent = `${panel.current + 1} / ${panel.anchors.length}`;
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      this.initialized = false;
      this.panels?.forEach(panel => { panel.markerSignature = undefined; });
    }
  }
  customElements.define('gebov-occasion-tabs', GebovOccasionTabs);
})();
